// Hermes use case — RunAgent: drive a run's FULL lifecycle from the .app.
//
//   start (POST → queued) → claim (PATCH running, CAS) → execute → finalize.
//
// The claim→execute→finalize core is exported as `driveRun` so the fan-out
// driver (fanOutAndMerge) can drive ALREADY-CREATED child runs through the
// exact same lifecycle; `runAgent` is just startRun + driveRun.
//
// All deps are injected ports (Dependency Rule: this layer imports nothing
// from infrastructure):
//   api      — hermesApiClient shape (startRun/getRun/patchRunState/cancelRun);
//              non-2xx throws carry `.status` (+`.code`)
//   executor — ExecutionPort: run(run, agent, { onEvent, signal }) → result
//   store    — live sink: setLive(runId, bool) + appendEvent(runId, evt)
//   clock    — () => ms epoch (injectable for tests)
//
// 409 semantics (the server is the arbiter, via CAS on the state column):
//   - claim PATCH 409  → another executor won the run → { claimed:false }
//   - finalize 409     → a concurrent cancel already made the run terminal —
//                        re-fetch the AUTHORITATIVE record (api.getRun) so the
//                        returned run is never stale; if the refetch fails the
//                        last-known record is returned with `stale: true`
// The live flag is ALWAYS cleared (try/finally), even when finalize throws.

// Finalize PATCH with the 409-swallow rule — the ONE place that owns the
// 409-refetch contract (shared with the fan-out coordinator, fanOutAndMerge).
// Returns { run, stale }: `run` is the patched record, or — when a concurrent
// cancel won — the refetched authoritative record; `run: null, stale: true`
// when even the refetch failed.
export function makePatchFinal(api) {
  return async function patchFinal(runId, patch) {
    try {
      return { run: await api.patchRunState(runId, patch), stale: false };
    } catch (e) {
      if (e?.status !== 409) throw e;
      try {
        const detail = await api.getRun(runId);
        return { run: detail?.run ?? null, stale: !detail?.run };
      } catch {
        return { run: null, stale: true };
      }
    }
  };
}

// Auto-append default is a no-op: the application layer never imports
// hermesApiClient (the architecture gate forbids it). runWiring (the
// composition root) injects the real `hermesApi.appendMemory`. With the no-op
// default, the run lifecycle works unchanged — it simply records no memory.
const noAppend = async () => {};

export function makeRunAgent({ api, executor, store, clock = Date.now, appendMemory = noAppend } = {}) {
  const patchFinal = makePatchFinal(api);

  /**
   * Drive an ALREADY-CREATED run (queued) through claim → execute → finalize.
   * Works for root runs (via runAgent) and for fan-out children alike.
   * Returns:
   *   { claimed:false, run }                      — lost the claim race
   *   { claimed:true, run, result }               — finalized (run authoritative)
   *   { claimed:true, run, result, stale:true }   — finalize 409 AND the
   *     refetch failed: `run` is the last-known record, possibly stale
   */
  async function driveRun(run, agentRecord, { signal } = {}) {
    // 1. Claim. A 409 means another executor claimed it first — that is a
    //    normal outcome of the race, not an error.
    let claimedRun;
    try {
      claimedRun = await api.patchRunState(run.id, { state: 'running', startedAt: clock() });
    } catch (e) {
      if (e?.status === 409) return { claimed: false, run };
      throw e;
    }

    // 2-4. Execute with the live store attached; finalize per the result.
    store.setLive(run.id, true);
    const onEvent = (evt) => store.appendEvent(run.id, evt);
    let finalRun = claimedRun;
    let stale = false;
    const applyFinal = (fin) => {
      if (fin.run) finalRun = fin.run;
      else if (fin.stale) stale = true;
    };
    try {
      let result;
      try {
        result = await executor.run(claimedRun, agentRecord, { onEvent, signal });
      } catch (e) {
        // Executor crashed (threw instead of returning a result). Mark the
        // run failed best-effort so it doesn't hang in `running`, then let
        // the original error surface.
        await patchFinal(run.id, {
          state: 'failed',
          error: e?.message || String(e),
          finishedAt: clock(),
        }).catch((patchErr) => {
          console.warn('[hermes] best-effort failed patch lost:', patchErr);
        });
        throw e;
      }

      if (result?.ok) {
        applyFinal(
          await patchFinal(run.id, {
            state: 'done',
            result: result.finalText ?? '',
            finishedAt: clock(),
            // E1 honesty: how many effecting actions the run took, and whether it
            // acted at all (the executor counted them from the live stream). A
            // done run with acted=0 renders as "concluiu sem ações", not green.
            actionToolCalls: result.actionToolCalls ?? 0,
            acted: result.acted ?? 0,
          }),
        );
        // Auto-append (D18): on success ONLY, record a run-summary memory so the
        // agent "learns" between runs (a later run recalls it). Best-effort and
        // AFTER the done PATCH — it must never block or fail the lifecycle. Skip
        // empty finalText (nothing learned). NOT on failed/cancelled/claim-lost.
        const finalText = result.finalText;
        if (typeof finalText === 'string' && finalText.trim()) {
          await appendMemory(agentRecord.id, {
            kind: 'run',
            name: (run.task || '').slice(0, 60),
            content: finalText.slice(0, 4000),
            sourceRunId: run.id,
          }).catch((e) => console.warn('[hermes] auto-append memory failed:', e));
        }
      } else if (result?.stopReason === 'aborted') {
        // Local Stop OR server-side cancel cascade — either way the run is
        // cancelled; a 409 here means the server already recorded it.
        applyFinal(await patchFinal(run.id, { state: 'cancelled', finishedAt: clock() }));
      } else {
        applyFinal(
          await patchFinal(run.id, {
            state: 'failed',
            error: result?.error || result?.stopReason || 'unknown',
            finishedAt: clock(),
          }),
        );
      }

      return stale
        ? { claimed: true, run: finalRun, result, stale: true }
        : { claimed: true, run: finalRun, result };
    } finally {
      store.setLive(run.id, false);
    }
  }

  /**
   * Full lifecycle for a NEW run: enqueue (server snapshots root) + drive.
   * opts.onStarted(run) fires right after startRun succeeds (BEFORE the
   * claim) so the UI learns the run id early (run board deep-link, T08).
   * A throwing callback never breaks the lifecycle (best-effort).
   */
  async function runAgent(agentRecord, task, opts = {}) {
    const run = await api.startRun(agentRecord.id, task);
    try {
      opts.onStarted?.(run);
    } catch {
      /* best-effort UI hook */
    }
    const out = await driveRun(run, agentRecord, opts);
    // Multi-channel outbound (D20): a manual ROOT run with a channel reports its
    // result back too (the backend startRun stamped source_channel_id). Same
    // seam the Mac dispatch path uses (useRemoteRunner), here for in-app runs.
    // Best-effort: a failure must NEVER fail the run (already terminal); a
    // channel-less run answers {posted:false} harmlessly. ROOT-only — driveRun
    // (children/fan-out) does NOT deliver.
    if (typeof api.requestOutbound === 'function') {
      try {
        await api.requestOutbound(run.id);
      } catch {
        /* outbound hiccup never fails the run */
      }
    }
    return out;
  }

  return { runAgent, driveRun };
}

/** Cancel passthrough — the UI's Cancel button. Returns { run, cancelledChildren }. */
export function makeCancelRun({ api } = {}) {
  return (runId) => api.cancelRun(runId);
}
